import {
  DISTANCE_UNITS,
  IAddClusterSourceParams,
  IGetPointLikeResult,
  IMapImage,
  IMapInfo,
  POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS
} from "form/map/model"
import { state } from "form/map/state"
import { getDistanceUnit, isImperialDistances } from "form/tools/geo-targets"
import { Feature, Polygon } from "geojson"
import { IBoundaryBox, ICircle, ILocation } from "geometry/model"
import { geometrySVC, getTurf } from "geometry/service"
import { GeoJSONSource, GeoJSONSourceRaw, LngLatBounds, Map, Point, PointLike } from "mapbox-gl"
import { MAP_COLOR } from "ui/model"
import { changeGeoTargetIsImperial, resetGeoTargetsMap } from "./geo-targets"
import { changeInventoryIsImperial, resetInventoryMap } from "./inventory"
import { getScaleDenominatorFromMetersPerPixel } from "./scale"

export function getMapboxAccessToken(): string {
  return MAPBOX_API_KEY
}

export function getLocationFromMabBoxEvent(event: any): ILocation {
  return {
    x: event.lngLat.lng,
    y: event.lngLat.lat
  }
}

export function setSourceData(map: Map, sourceId: string, features: Feature[]): void {
  const source: GeoJSONSource = map.getSource(sourceId) as GeoJSONSource
  if (!source) {
    return
  }
  source.setData({
    type: "FeatureCollection",
    features
  })
}

export function getMapMouseCursor(map: Map): string {
  return map.getCanvas().style.cursor
}

export function setMapMouseCursor(map: Map, cursor: string) {
  map.getCanvas().style.cursor = cursor
}

export function getLatitudeInRadian(map: Map, latitude?: number): number {
  return ((typeof latitude !== "number" ? map.getCenter().lat : latitude) * Math.PI) / 180
}

export function getMapExtent(map: Map): IBoundaryBox {
  const bounds: mapboxgl.LngLatBounds = map.getBounds()
  return {
    ne: getLocationFromMapBoxLngLat(bounds.getNorthEast()),
    sw: getLocationFromMapBoxLngLat(bounds.getSouthWest())
  }
}

export function getLocationFromMapBoxLngLat(point: mapboxgl.LngLat): ILocation {
  return {
    x: point.lng,
    y: point.lat
  }
}

export function getMapInfo(map: Map): IMapInfo {
  return {
    center: getLocationFromMapBoxLngLat(map.getCenter()),
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
    scale: getScaleDenominatorFromMetersPerPixel({
      map,
      latitude: map.getCenter().lat,
      zoom: map.getZoom()
    }),
    boundaryBox: getMapExtent(map)
  }
}

export const getFeaturesExtent = (features: Feature[]): LngLatBounds => {
  const bbox = new LngLatBounds()
  if (features.length === 0) {
    return bbox
  }
  const turf = getTurf()
  for (const feature of features) {
    const bound = turf.bbox(turf.simplify(feature as any))
    if (
      typeof bound[0] === "number" &&
      !Number.isNaN(bound[0]) &&
      typeof bound[1] === "number" &&
      !Number.isNaN(bound[1]) &&
      typeof bound[2] === "number" &&
      !Number.isNaN(bound[2]) &&
      typeof bound[3] === "number" &&
      !Number.isNaN(bound[3])
    ) {
      bbox.extend(bound as [number, number, number, number])
    }
  }
  return bbox
}

export function panTo(map: Map, center: ILocation): void {
  panAndZoomTo(map, center, map.getZoom())
}

export function zoomTo(map: Map, zoom: number): void {
  const center = map.getCenter()
  panAndZoomTo(map, { x: center.lng, y: center.lat }, zoom)
}

export function zoomToRect(map: Map, bbox: IBoundaryBox): void {
  map.fitBounds(
    [
      [bbox.sw.x, bbox.sw.y],
      [bbox.ne.x, bbox.ne.y]
    ],
    {
      animate: true
    }
  )
}

export function panAndZoomTo(map: Map, center: ILocation, zoom: number): void {
  map.flyTo({
    animate: true,
    center: { lng: center.x, lat: center.y },
    zoom,
    speed: 2,
    curve: 1
  })
}

export function fitFeaturesOnMap(map: Map, features: Feature[]): void {
  if (!features || features.length === 0) {
    return
  }
  const bbox = geometrySVC.getBboxFromFeatures(features)
  map.fitBounds(
    [
      { lng: bbox.sw.x, lat: bbox.sw.y },
      { lng: bbox.ne.x, lat: bbox.ne.y }
    ],
    {
      padding: { top: 100, bottom: 100, left: 100, right: 100 },
      maxZoom: 12
    }
  )
}

export function resizeAllMaps(): void {
  if (state.geoTargets.map) {
    state.geoTargets.map.resize()
  }
  if (state.inventory.map) {
    state.inventory.map.resize()
  }
}

export function addClusterSource(params: IAddClusterSourceParams) {
  const { map, sourceId, layers, features } = params
  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features
    },
    maxzoom: 24,
    cluster: true,
    clusterMaxZoom: 14, // Max zoom to cluster points on
    clusterRadius: 50
  } as GeoJSONSourceRaw)

  map.addLayer({
    id: getClusterCircleLayerId(sourceId),
    type: "circle",
    source: sourceId,
    filter: ["has", "point_count"],
    paint: {
      // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
      // with three steps to implement three types of circles:
      //   * Blue, 20px circles when point count is less than 100
      //   * Yellow, 30px circles when point count is between 100 and 750
      //   * Pink, 40px circles when point count is greater than or equal to 750
      "circle-color": MAP_COLOR, // ["step", ["get", "point_count"], "#51bbd6", 100, "#f1f075", 750, "#f28cb1"],
      "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40]
    }
  })

  map.addLayer({
    id: `${sourceId}-cluster-count`,
    type: "symbol",
    source: sourceId,
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
      "text-size": 12
    }
  })

  for (const layerParams of layers) {
    const { layer, before } = layerParams
    layer.source = sourceId
    if (!layer.filter) {
      layer.filter = getDefaultNoClusterFeatureFilter()
    } else {
      layer.filter = ["all", getDefaultNoClusterFeatureFilter(), layer.filter]
    }
    map.addLayer(layer, before)
  }
}

export function getDefaultNoClusterFeatureFilter(): any[] {
  return ["!", ["has", "point_count"]]
}

export function getRenderedFeatures(
  map: Map,
  layerIds: string[],
  params?: ILocation | IBoundaryBox | ICircle
): Feature[] {
  if (layerIds.length === 0) {
    return []
  }
  let circleFeature: Feature<Polygon> | undefined
  let pointLike: PointLike | [PointLike, PointLike] | undefined
  if (typeof params === "object") {
    const result = getPointLikeFromFilter(map, params)
    pointLike = result?.pointLike
    if (result?.circleFeature) {
      circleFeature = result?.circleFeature
    }
  }
  if (!circleFeature) {
    return map.queryRenderedFeatures(pointLike, { layers: layerIds })
  } else {
    return map
      .queryRenderedFeatures(pointLike, { layers: layerIds })
      .filter(feature => geometrySVC.polygonIntersect(circleFeature as any, feature))
  }
}

export function getClusterCircleLayerId(sourceId: string): string {
  return `${sourceId}-clusters`
}

export function isClusterFeature(feature: GeoJSON.Feature): boolean {
  return feature.properties?.cluster === true
}

export const loadMapImages = (map: Map, images: IMapImage[]): Promise<any> =>
  Promise.allSettled(images.map(i => loadMapImage(map, i.name, i.url)))

export function resetAllMaps(): void {
  resetGeoTargetsMap()
  resetInventoryMap()
}

export const changeMapsDistanceUnit = (newDistanceUnit: DISTANCE_UNITS, oldDistanceUnit: DISTANCE_UNITS): void => {
  const newIsImperial = isImperialDistances(newDistanceUnit)
  if (newIsImperial === isImperialDistances(oldDistanceUnit)) {
    return
  }
  changeGeoTargetIsImperial(newIsImperial)
  changeInventoryIsImperial(newIsImperial)
}

function loadMapImage(map: Map, imageName: string, iconUrl: string): Promise<void> {
  return new Promise<void>(resolve => {
    try {
      map.loadImage(iconUrl, (error, image: any) => {
        if (error) {
          console.error("An error occured while loading add coordinate icon", error)
          return resolve()
        }
        map.addImage(imageName, image)
        resolve()
      })
    } catch (error) {
      console.error("An error occured while loading add coordinate icon", error)
      return resolve()
    }
  })
}

function toPixelPoint(map: Map, location: ILocation): mapboxgl.Point {
  return map.project({
    lng: location.x,
    lat: location.y
  })
}

function getPointLikeFromFilter(map: Map, filter: ILocation | IBoundaryBox | ICircle): IGetPointLikeResult {
  if (filter.hasOwnProperty("sw")) {
    const boundaryBox: IBoundaryBox = filter as IBoundaryBox
    return { pointLike: [toPixelPoint(map, boundaryBox.sw), toPixelPoint(map, boundaryBox.ne)] as [Point, Point] }
  } else if (filter.hasOwnProperty("x")) {
    // we want to use a padded bounding box instead of just a point to add a tolerance
    // for very fine lines or points
    const pointLike = toPixelPoint(map, filter as ILocation)
    // screen pixels coordinates have their origin at the top-left corner
    // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#queryrenderedfeatures
    // a 5 pixels padding produces a 10x10 pixel bouding box centered on the passed point
    const pointLikeBottomRight: [number, number] = [
      pointLike.x - POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS,
      pointLike.y + POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS
    ]
    const pointLikeTopLeft: [number, number] = [
      pointLike.x + POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS,
      pointLike.y - POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS
    ]
    return { pointLike: [pointLikeBottomRight, pointLikeTopLeft] }
  } else if (filter.hasOwnProperty("radius")) {
    const circleFeature = geometrySVC.getCircleFeature(
      (filter as ICircle).center,
      (filter as ICircle).radius,
      getDistanceUnit()
    )
    const boundaryBox: IBoundaryBox = geometrySVC.getBboxFromFeature(circleFeature)
    return {
      pointLike: [toPixelPoint(map, boundaryBox.sw), toPixelPoint(map, boundaryBox.ne)],
      circleFeature
    }
  }
  throw Error("Unsupported filter")
}
